import { useEffect, useRef, useState } from "react";
import { Loader2, Wifi, WifiOff } from "lucide-react";
import { api } from "../lib/apiClient";
import { useNetStatus, wireNetStatus } from "../lib/netStatus";

/**
 * OfflineBanner — Jun 2026 · Offline Grace.
 *
 * Slim strip pinned to the bottom of every screen. While the connection is
 * down it reads "Reconnecting…" (the page keeps showing its last-known data
 * instead of erroring out); when it recovers it flashes "Back online" and
 * gets out of the way.
 */
const BACK_ONLINE_MS = 2500;

export default function OfflineBanner() {
  const offline = useNetStatus();
  const [showBack, setShowBack] = useState(false);
  // Ref, not state: keeping it out of the effect's deps means the
  // auto-hide timer isn't torn down by its own state update.
  const wasOfflineRef = useRef(false);

  useEffect(() => {
    wireNetStatus(api);
  }, []);

  useEffect(() => {
    if (offline) {
      wasOfflineRef.current = true;
      setShowBack(false);
      return undefined;
    }
    if (!wasOfflineRef.current) return undefined;
    wasOfflineRef.current = false;
    setShowBack(true);
    const t = setTimeout(() => setShowBack(false), BACK_ONLINE_MS);
    return () => clearTimeout(t);
  }, [offline]);

  if (offline) {
    return (
      <div
        role="status"
        aria-live="polite"
        data-testid="offline-banner"
        className="fixed bottom-0 left-0 right-0 z-[70] flex items-center justify-center gap-2 px-4 py-2 bg-[#3F2E00] text-amber-100 text-[10px] uppercase tracking-[0.25em] font-bold"
      >
        <WifiOff className="w-3.5 h-3.5" aria-hidden />
        <Loader2 className="w-3 h-3 animate-spin" aria-hidden />
        Reconnecting…
        <span className="normal-case tracking-normal font-normal opacity-80 hidden sm:inline">
          your work is safe — this page is showing the last data we loaded
        </span>
      </div>
    );
  }

  if (!showBack) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="back-online-banner"
      className="fixed bottom-0 left-0 right-0 z-[70] flex items-center justify-center gap-2 px-4 py-2 bg-emerald-800 text-emerald-50 text-[10px] uppercase tracking-[0.25em] font-bold"
    >
      <Wifi className="w-3.5 h-3.5" aria-hidden />
      Back online
    </div>
  );
}
